import {
  Component, Prop, h, State, ComponentInterface, Method,
} from '@stencil/core';
import { TCustomPrice, TPesCatalogPageType, TCTAButtonSettings, TProductCtas, productCtas, CAASContentOverviewSlotName, FeatureStatus, deepMerge, PBSConfigs } from '@pes-ui/utils';
import { PesRouterStore } from '@pes-ui/components/dist/collection';

import type { TSearchParams, FeatureAttributes } from '@pes-ui/utils';

import { ConfiguratorHandler } from '@pes-ui/components';

const defaultFeatures = {
  productCtas,
};

export type TFeatures = {
  productCtas?: TProductCtas;
};

@Component({
  tag: 'pes-catalog',
})
export class PesCatalog implements ComponentInterface {
  /**
   * Rest API host.
   */
  @Prop() host: string;

  /**
   * Language code.
   * @example en or fr.
   */
  @Prop({ mutable: true }) language = 'en';

  /**
   * Country code.
   * @example us or ww or fr.
   */
  @Prop({ mutable: true }) country = 'us';

  /**
   * Catalog level page to show.
   */
  @Prop({ mutable: true }) page: TPesCatalogPageType = '';

  /**
   * Catalog page id.
   */
  @Prop({ mutable: true }) pageId: string;

  /**
   * Show custom price for all pages.
   */
  @Prop({ mutable: true }) showCustomPrice = false;

  /**
   * Show PES price for all pages.
   */
  @Prop({ mutable: true }) showCatalogPrice = true;

  /**
   * Custom price data.
   */
  @Prop() customPrice: TCustomPrice;

  /**
   * Callback function for Breadcrumbs
   * @todo update with event to use instead and mark as deprecated.
   */
  @Prop() returnBreadcrumbs: (list: unknown[]) => void;

  /**
   * Show breadcrumbs on PES pages.
   */
  @Prop() showBreadcrumbs = false;

  /**
   * This parameter contains configuration for the document listing component (DDC App Source).
   * For more information, please visit
   * https://confluence.se.com/pages/viewpage.action?pageId=128510823.
   */
  @Prop() ddcAppSource?: string;

  /**
   * This parameter contains configuration for the document download center url
   * displayed as an action block on All Products page.
   * For more information, please visit
   * https://confluence.se.com/display/PESV3/SE+All+Products+component.
   */
  @Prop() ddcAllProductsUrl?: string;

  /**
   * Determines which version of the label to display, short or long.
   */
  @Prop() substitutionLabelShort: boolean;

  /**
   * Tab id for scroll to a tab on PDP.
   * @example documents
   */
  @Prop({ mutable: true }) tabId?: string;

  /**
   * If show Documents CTA and documents list in product-card
   */
  @Prop() showDocuments?: boolean;

  /**
   * PES Catalogue features
   */
  @Prop() features: TFeatures = defaultFeatures;

  /**
   * Defines whether to show Buy Online button
   */
  @Prop({ mutable: true }) showBuyOnline = false;

  /**
   * Defines whether to show compare button
   */
  @Prop({ mutable: true }) showCompare = false;

  /**
   * Defines whether to show FAQ section
   */
  @Prop({ mutable: true }) showFaq = false;

  /**
   * Defines whether to show Recently Viewed section
   */
  @Prop({ mutable: true }) showRecentlyViewed = false;

  /**
   * Defines the context of the app: B2B or B2C
   */
  @Prop({ mutable: true }) siteType: 'b2b' | 'b2c' = 'b2b';

  /**
   * Defines the global configuration URL
   */
  @Prop({ attribute: 'global-configs-url' }) globalConfigsURL = 'https://www.se.com/product/configs/business-service/replatform-globals.json';

  /**
   * Defines if testing control panel is active or not
   */
  @Prop({ attribute: 'is-panel-active' }) isPanelActive = false;

  @Prop({ attribute: 'user-access-token' }) userAccessToken: string = null;

  @Prop({ attribute: 'user-profile' }) userProfile: string = null;

  @State() productCtas: TProductCtas = productCtas;

  /**
   * Object with url search parameters if exists
   * @example { filter: "business-10-smart-grid" }
   */
  @Prop({ mutable: true }) searchParams: TSearchParams = {};

  @State() globalConfigs: PBSConfigs;

  @State() APIList: string;

  @State() featureFlags: Record<string, FeatureStatus>;

  @State() isUnifiedCheckoutEnabled = false;

  /**
   * Updates Product CTAs lists with new product IDs
   * @param obj Object containing key value pairs where key is the Product CTA ID and value is the updated list of product IDs
   */
  @Method()
  async updateProductCtasList(obj: { [key: string]: string[] }): Promise<void> {
    if (this.isUnifiedCheckoutEnabled) return;

    const newProductCtas = { ...this.features.productCtas };
    Object.keys(obj).forEach((type) => {
      newProductCtas.buttons[type].list = obj[type];
    });
    this.productCtas = { ...newProductCtas };
  }

  /**
   * Updates Product CTAs state with new Product CTAs settings
   * @param obj new Product CTAs settings
   */
  @Method()
  async updateProductCtasButtons(obj: { [key: string]: TCTAButtonSettings }): Promise<void> {
    if (this.isUnifiedCheckoutEnabled) return;
    const newProductCtas = { ...this.features.productCtas };
    Object.keys(obj).forEach((type) => {
      deepMerge(newProductCtas.buttons[type], obj[type]);
    });
    this.productCtas = { ...newProductCtas };
  }

  constructor() {
    document.body.setAttribute('locale', this.getLocaleCode(this.country, this.language));
  }

  async componentWillLoad(): Promise<void> {
    if (PesRouterStore.get('usePesRouter')) {
      this.country = PesRouterStore.get('country');
      this.language = PesRouterStore.get('language');
      this.page = PesRouterStore.get('page');
      this.pageId = PesRouterStore.get('pageId');
      this.searchParams = PesRouterStore.get('searchParams');
    }
    document.body.setAttribute('locale', this.getLocaleCode(this.country, this.language));
    this.globalConfigs = await this.getPBSGlobalConfigs();
    const { featureFlags } = this.globalConfigs;
    this.featureFlags = featureFlags;
    window.isPBS = true;
    ConfiguratorHandler.setupGlobalConfigs(this.globalConfigs, this.country, this.language);
    const unifiedCheckoutFlag = ConfiguratorHandler.getSingleConfig('FeatureFlags')?.unifiedCheckout;
    this.isUnifiedCheckoutEnabled = ConfiguratorHandler.isFeatureFlagEnabled(
      unifiedCheckoutFlag || {} as FeatureAttributes,
    );
  }

  public getLocaleCode(country: string, language: string): string {
    return `${language}_${country.toUpperCase()}`;
  }

  private async getPBSGlobalConfigs(): Promise<PBSConfigs> {
    try {
      const rawResponse = await fetch(this.globalConfigsURL, {
        headers: { pragma: 'no-cache', 'cache-control': 'no-cache' },
      });
      return await (rawResponse.json() as Promise<PBSConfigs>);
    } catch (error) {
      console.error('Failed to fetch PBS configs ', error);
      return {
        api: {},
        featureFlags: undefined,
        LocalizedFeatures: {
          Substitutions: { locales: [] },
          UnifiedCheckout: { locales: [] },
          HyperPersonalization: { locales: [] },
        },
        Widgets: undefined,
      };
    }
  }

  render(): void {
    return (
      <pes-spryker-container
        host={!this.host ? '' : this.host}
        language={this.language}
        country={this.country}
        page={this.page}
        pageId={this.pageId}
        showCustomPrice={this.showCustomPrice}
        showCatalogPrice={this.showCatalogPrice}
        customPrice={this.customPrice}
        showBreadcrumbs={this.showBreadcrumbs}
        substitutionLabelShort={this.substitutionLabelShort}
        tabId={this.tabId}
        showDocuments={this.showDocuments}
        features={this.features}
        showBuyOnline={this.showBuyOnline}
        showCompare={this.showCompare}
        showFaq={this.showFaq}
        showRecentlyViewed={this.showRecentlyViewed}
        siteType={this.siteType}
        searchParams={this.searchParams}
        ddcProps={{
          appSource: this.ddcAppSource,
          userProfile: this.userProfile,
          idmsToken: this.userAccessToken,
        }}
        ddcAllProductsUrl={this.ddcAllProductsUrl}
        productCtas={this.productCtas}
        featureFlags={this.featureFlags}
      >
        <slot slot="discounted-price" name="discounted-price" />
        <slot slot="unified-checkout" name="unified-checkout" />
        <slot slot={CAASContentOverviewSlotName} name={CAASContentOverviewSlotName} />
      </pes-spryker-container>
    );
  }
}
